import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../utils/utils';
import { CalendarPopover, RDP_PRIMARY_TOKENS, useDayPickerLocale } from './ui/datePickerShared';
import 'react-day-picker/style.css';

export interface DateRangePickerProps {
    from: Dayjs | null;
    to: Dayjs | null;
    onChange: (from: Dayjs | null, to: Dayjs | null) => void;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    className?: string;
}

// Single-popover range picker for the admin dashboard. Replaces the previous
// two-MUI-DatePicker setup which required selecting From and To independently
// (lots of clicks, popper cut off on mobile). One calendar, click-from →
// click-to, done. Built on react-day-picker's `mode="range"`.
export function DateRangePicker({ from, to, onChange, minDate, maxDate, className }: DateRangePickerProps) {
    const { t } = useTranslation();
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const [open, setOpen] = React.useState(false);

    const locale = useDayPickerLocale();

    const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchor(event.currentTarget);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSelect = (range: { from?: Date, to?: Date } | undefined) => {
        const nextFrom = range?.from ? dayjs(range.from) : null;
        const nextTo = range?.to ? dayjs(range.to) : null;
        onChange(nextFrom, nextTo);
        // Close once the user has selected a complete range.
        if (nextFrom && nextTo) {
            setOpen(false);
        }
    };

    const formatLabel = (d: Dayjs | null, fallback: string) =>
        d ? d.format('D/MM/YYYY') : fallback;

    const hasFrom = !!from;
    const hasTo = !!to;
    const isComplete = hasFrom && hasTo;

    return (
        <div className={cn('relative inline-block', className)}>
            <button
                type={'button'}
                onClick={handleTriggerClick}
                className={cn(
                    'flex items-center gap-2 cursor-pointer',
                    'bg-white rounded-full px-4 sm:px-6 py-2 font-medium text-sm',
                    'min-w-[220px] max-w-[280px] transition-shadow duration-150',
                    open ? 'shadow-md' : 'shadow-sm',
                )}
                aria-haspopup={'dialog'}
                aria-expanded={open}
            >
                <CalendarIcon className={'w-4 h-4 text-foreground/60 shrink-0'} aria-hidden={'true'} />
                <span className={'flex items-center gap-1 select-none'}>
                    <span className={hasFrom ? 'text-foreground' : 'text-gray-400'}>
                        {formatLabel(from, t('dashboard.from'))}
                    </span>
                    <span className={'text-foreground/60'}>–</span>
                    <span className={hasTo ? 'text-foreground' : 'text-gray-400'}>
                        {formatLabel(to, t('dashboard.to'))}
                    </span>
                </span>
            </button>

            <CalendarPopover anchorEl={anchor} open={open} onClose={handleClose}>
                <DayPicker
                    mode={'range'}
                    selected={{
                        from: from ? from.toDate() : undefined,
                        to: to ? to.toDate() : undefined,
                    }}
                    onSelect={handleSelect}
                    locale={locale}
                    disabled={[
                        ...(minDate ? [{ before: minDate.toDate() }] : []),
                        ...(maxDate ? [{ after: maxDate.toDate() }] : []),
                    ]}
                    captionLayout={'dropdown'}
                    startMonth={minDate ? minDate.toDate() : undefined}
                    endMonth={maxDate ? maxDate.toDate() : undefined}
                    numberOfMonths={1}
                    showOutsideDays
                    autoFocus
                    style={RDP_PRIMARY_TOKENS}
                />
                {isComplete && (
                    <div className={'mt-2 flex justify-end'}>
                        <button
                            type={'button'}
                            className={'text-xs text-foreground/50 hover:text-foreground transition-colors px-2 py-1'}
                            onClick={() => onChange(null, null)}
                        >
                            {t('dashboard.clear_range')}
                        </button>
                    </div>
                )}
            </CalendarPopover>
        </div>
    );
}

export default DateRangePicker;
